import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaCode, FaMobile, FaPalette, FaServer, FaChartLine, FaRobot,
  FaShieldAlt, FaBolt, FaUsers, FaAward,
} from 'react-icons/fa';
import { HiArrowRight, HiSparkles, HiCheckCircle } from 'react-icons/hi';
import heroImage from '../assets/hero.jpg';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

/* ── Shared animation variants ── */
const scaleIn = {
  hidden: { opacity: 0, scale: 0.88 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const Home = () => {
  const statsRef    = useRef(null);
  const servicesRef = useRef(null);
  const navigate    = useNavigate();

  /* ── Testimonial GSAP refs ── */
  const testimonialsWrapRef = useRef(null);
  const testimonialRefs     = useRef([]);

  /* ── Process GSAP refs ── */
  const processWrapRef    = useRef(null);
  const processCardRefs   = useRef([]);
  const processNumberRefs = useRef([]);

  /* ── Hero GSAP refs ── */
  const heroSectionRef = useRef(null);
  const heroBadgeRef   = useRef(null);
  const heroH1Ref      = useRef(null);
  const heroParaRef    = useRef(null);
  const heroCtaRef     = useRef(null);
  const heroImgRef     = useRef(null);
  const heroGlowRef    = useRef(null);

  /* ── Hero entrance timeline ── */
  useLayoutEffect(() => {
    const els = [heroBadgeRef, heroH1Ref, heroParaRef, heroCtaRef].map(r => r.current).filter(Boolean);
    const img = heroImgRef.current;

    if (prefersReducedMotion()) {
      gsap.set([...els, img].filter(Boolean), { opacity: 1, y: 0, scale: 1, x: 0 });
      return;
    }

    gsap.set(els, { opacity: 0, y: 40, willChange: 'transform' });
    if (img) gsap.set(img, { opacity: 0, scale: 1.06, x: 30, willChange: 'transform' });

    const tl = gsap.timeline({ delay: 0.1 });
    tl.to(els, { opacity: 1, y: 0, duration: 0.9, stagger: 0.14, ease: 'power4.out' });
    if (img) {
      tl.to(img, { opacity: 1, scale: 1, x: 0, duration: 1.0, ease: 'power3.out' }, '-=0.55');
    }

    return () => tl.kill();
  }, []);

  /* ── Hero image: parallax on scroll + cursor magnetic glow ── */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const section = heroSectionRef.current;
    const img     = heroImgRef.current;
    const glow    = heroGlowRef.current;
    if (!section || !img) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
        onUpdate: ({ progress }) => {
          gsap.to(img, { y: progress * 60, duration: 0.3, overwrite: 'auto' });
        },
      });
    }, heroSectionRef);

    const onMove = (e) => {
      if (!glow) return;
      const rect = section.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width)  * 100;
      const y = ((e.clientY - rect.top)  / rect.height) * 100;
      gsap.to(glow, {
        background: `radial-gradient(600px circle at ${x}% ${y}%, rgba(9,132,227,0.12), rgba(168,0,255,0.06) 40%, transparent 70%)`,
        duration: 0.6, ease: 'power2.out', overwrite: 'auto',
      });
    };

    section.addEventListener('mousemove', onMove);
    return () => {
      ctx.revert();
      section.removeEventListener('mousemove', onMove);
    };
  }, []);

  /* Hero floating cards: continuous gentle bob (after Framer's entrance) */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const cards = heroSectionRef.current?.querySelectorAll('.hero-float');
    if (!cards || !cards.length) return;
    const tweens = [];
    cards.forEach((c, i) => {
      tweens.push(gsap.to(c, {
        y: '+=8',
        duration: 2.6 + i * 0.4,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: 1.6 + i * 0.15,
      }));
    });
    return () => tweens.forEach(t => t.kill());
  }, []);

  /* Stats: coordinated GSAP stagger entrance (scoped, reduced-motion aware) */
  useEffect(() => {
    if (!statsRef.current) return;
    const ctx = gsap.context(() => {
      const items = gsap.utils.toArray('.stat-item');
      if (!items.length) return;
      if (prefersReducedMotion()) {
        gsap.set(items, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(items, { opacity: 0, y: 28, willChange: 'transform' });
      ScrollTrigger.create({
        trigger: statsRef.current,
        start: 'top 82%',
        once: true,
        onEnter: () => {
          gsap.to(items, {
            opacity: 1, y: 0,
            duration: 0.9, stagger: 0.1, ease: 'power3.out',
          });
        },
      });
    }, statsRef);
    return () => ctx.revert();
  }, []);

  /* Why Choose Us: GSAP stagger entrance + smooth lift on hover */
  const whyChooseRef = useRef(null);
  useEffect(() => {
    if (!whyChooseRef.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.why-card');
      if (!cards.length) return;
      if (prefersReducedMotion()) {
        gsap.set(cards, { opacity: 1, y: 0, scale: 1 });
        return;
      }
      gsap.set(cards, { opacity: 0, y: 36, scale: 0.96, willChange: 'transform' });
      ScrollTrigger.create({
        trigger: whyChooseRef.current,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          gsap.to(cards, {
            opacity: 1, y: 0, scale: 1,
            duration: 0.95, stagger: 0.1, ease: 'power3.out',
          });
        },
      });
      // Smooth hover lift (replace Tailwind's hard transition for consistency)
      cards.forEach((card) => {
        const onEnter = () => gsap.to(card, { y: -6, scale: 1.015, duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
        const onLeave = () => gsap.to(card, { y: 0,  scale: 1,     duration: 0.6, ease: 'power3.out', overwrite: 'auto' });
        card.addEventListener('mouseenter', onEnter);
        card.addEventListener('mouseleave', onLeave);
      });
    }, whyChooseRef);
    return () => ctx.revert();
  }, []);

  const services = [
    { icon: <FaCode />,      title: 'Web Development',       description: 'We create responsive websites optimized for performance, SEO, and conversion using modern frameworks and technologies.', color: 'from-nexcess-blue to-nexcess-cyan',    accent: '9,132,227'   },
    { icon: <FaMobile />,    title: 'Mobile App Development', description: 'Build native and cross-platform mobile applications that deliver exceptional user experiences across all devices.',       color: 'from-nexcess-cyan to-nexcess-blue',    accent: '0,176,255'   },
    { icon: <FaPalette />,   title: 'UI/UX Design',           description: 'Transform your digital presence with intuitive, user-friendly designs that engage and delight your audience.',           color: 'from-nexcess-purple to-nexcess-blue',  accent: '168,0,255'   },
    { icon: <FaServer />,    title: 'Cloud Services',         description: 'Scalable cloud solutions tailored to your business needs, including AWS, Azure, and Google Cloud integrations.',          color: 'from-nexcess-blue to-nexcess-purple',  accent: '9,132,227'   },
    { icon: <FaChartLine />, title: 'Digital Marketing',      description: 'Enhance your online presence with SEO, social media marketing, and strategic digital campaigns.',                        color: 'from-nexcess-cyan to-nexcess-purple',  accent: '0,176,255'   },
    { icon: <FaRobot />,     title: 'AI Integration',         description: 'Implement cutting-edge AI solutions to automate processes and create smarter applications.',                              color: 'from-nexcess-purple to-nexcess-cyan',  accent: '168,0,255'   },
  ];

  const stats = [
    { value: '204+', label: 'Projects Completed' },
    { value: '200+', label: 'Happy Clients' },
    { value: '5+', label: 'Years Experience' },
    { value: '10+', label: 'Team Members' },
  ];

  const whyChoose = [
    { icon: <FaBolt />, title: 'Cutting-edge Technology', description: 'Latest tools and frameworks to keep you ahead of the curve.' },
    { icon: <FaShieldAlt />, title: 'Dedicated Support', description: '24/7 expert assistance from a team that genuinely cares.' },
    { icon: <FaUsers />, title: 'Agile Methodology', description: 'A flexible and adaptive approach tuned to your timeline.' },
    { icon: <FaAward />, title: 'Results-Driven', description: 'Focused on tangible business outcomes, not just deliverables.' },
  ];

  const process = [
    { step: '01', title: 'Discover', description: 'We start by understanding your vision, goals, and the people you serve.' },
    { step: '02', title: 'Design', description: 'Crafting elegant interfaces and architectures that feel intuitive.' },
    { step: '03', title: 'Develop', description: 'Engineering scalable, performant code with modern best practices.' },
    { step: '04', title: 'Deliver', description: 'Launch with confidence — followed by ongoing support and growth.' },
  ];

  const testimonials = [
    {
      text: "Nexcess Infotech transformed our online presence with a stunning website that perfectly captures our brand. Their team was professional, responsive, and delivered beyond our expectations.",
      author: 'Sarah Johnson',
      position: 'Marketing Director, TechSolutions Inc.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80',
    },
    {
      text: 'The mobile app developed by Nexcess has significantly increased our customer engagement and sales. Their expertise in UI/UX design and development is truly impressive.',
      author: 'Michael Chen',
      position: 'CEO, DigitalGrowth Ltd.',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80',
    },
    {
      text: "Working with Nexcess on our cloud migration project was a seamless experience. Their team's technical knowledge and attention to detail ensured a smooth transition with zero downtime.",
      author: 'Emily Davis',
      position: 'CTO, InnovateSystems',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80',
    },
  ];

  // ── Holographic card effect ──
  // Each card's accent RGB is read from a data-attribute set in JSX
  useEffect(() => {
    let handlers = [];
    const timer = setTimeout(() => {
      const wrappers = document.querySelectorAll('.holo-card-wrap');
      handlers = Array.from(wrappers).map((wrap) => {
        const inner = wrap.querySelector('.holo-card-inner');
        const glare = wrap.querySelector('.holo-glare');
        const tint  = wrap.querySelector('.holo-tint');
        if (!inner || !glare || !tint) return null;

        // Per-card accent colour stored as a data attribute
        const accent = wrap.dataset.accent || '9,132,227';

        const onEnter = () => {
          gsap.to(tint,  { opacity: 0.07, duration: 0.4, ease: 'power2.out' });
          gsap.to(glare, { opacity: 1,    duration: 0.4, ease: 'power2.out' });
        };

        const onMove = (e) => {
          const rect = wrap.getBoundingClientRect();
          const x  = e.clientX - rect.left;
          const y  = e.clientY - rect.top;
          const cx = rect.width  / 2;
          const cy = rect.height / 2;

          gsap.to(inner, {
            rotationX: ((y - cy) / cy) * -10,
            rotationY: ((x - cx) / cx) *  10,
            transformPerspective: 900,
            ease: 'power2.out', duration: 0.22, overwrite: 'auto',
          });

          // Glare: soft pastel spot of the card's own accent colour
          const px = (x / rect.width)  * 100;
          const py = (y / rect.height) * 100;
          glare.style.background = [
            `radial-gradient(ellipse 55% 45% at ${px}% ${py}%,`,
            ` rgba(${accent},0.18),`,
            ` transparent 70%)`,
          ].join('');
        };

        const onLeave = () => {
          gsap.to(inner, {
            rotationX: 0, rotationY: 0, scale: 1,
            ease: 'elastic.out(1,0.4)', duration: 0.8, overwrite: 'auto',
          });
          gsap.to([tint, glare], { opacity: 0, duration: 0.4, ease: 'power2.out' });
        };

        const onDown = () => gsap.to(inner, { scale: 0.97, duration: 0.12, ease: 'power2.out', overwrite: 'auto' });
        const onUp   = () => gsap.to(inner, { scale: 1.00, duration: 0.20, ease: 'power2.out', overwrite: 'auto' });

        wrap.addEventListener('mouseenter',  onEnter);
        wrap.addEventListener('mousemove',   onMove);
        wrap.addEventListener('mouseleave',  onLeave);
        wrap.addEventListener('mousedown',   onDown);
        wrap.addEventListener('mouseup',     onUp);
        return { wrap, onEnter, onMove, onLeave, onDown, onUp };
      }).filter(Boolean);
    }, 400);

    return () => {
      clearTimeout(timer);
      handlers.forEach(({ wrap, onEnter, onMove, onLeave, onDown, onUp }) => {
        wrap.removeEventListener('mouseenter',  onEnter);
        wrap.removeEventListener('mousemove',   onMove);
        wrap.removeEventListener('mouseleave',  onLeave);
        wrap.removeEventListener('mousedown',   onDown);
        wrap.removeEventListener('mouseup',     onUp);
      });
    };
  }, []);

  /* ── Testimonials: smooth blur-clear rise + soft float + gentle 3D hover lift ── */
  useEffect(() => {
    const wrap  = testimonialsWrapRef.current;
    const cards = testimonialRefs.current.filter(Boolean);
    if (!wrap || !cards.length) return;

    gsap.set(wrap, { perspective: 1200 });
    gsap.set(cards, {
      opacity: 0,
      y: 60,
      scale: 0.94,
      filter: 'blur(14px)',
      willChange: 'transform, filter, opacity',
    });

    // Smooth staggered entrance — long, easy easing
    const entranceST = ScrollTrigger.create({
      trigger: wrap,
      start: 'top 82%',
      once: true,
      onEnter: () => {
        gsap.to(cards, {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: 1.4,
          ease: 'power3.out',
          stagger: 0.18,
          onComplete: () => {
            // Continuous gentle float — different phase per card
            cards.forEach((card, i) => {
              gsap.to(card, {
                y: '+=8',
                duration: 3.2 + i * 0.4,
                ease: 'sine.inOut',
                yoyo: true,
                repeat: -1,
                delay: i * 0.4,
              });
            });
          },
        });

        // Stars: sequential pop within each card
        cards.forEach((card, i) => {
          const stars = card.querySelector('.t-stars');
          if (stars) {
            gsap.fromTo(stars,
              { scale: 0.6, opacity: 0 },
              {
                scale: 1, opacity: 1,
                duration: 0.6, ease: 'back.out(2)',
                delay: 0.4 + i * 0.18,
              }
            );
          }
        });
      },
    });

    // Gentle 3D hover lift — soft, smooth, not aggressive
    const handlers = cards.map((card) => {
      const onMove = (e) => {
        const r = card.getBoundingClientRect();
        const cx = e.clientX - (r.left + r.width / 2);
        const cy = e.clientY - (r.top  + r.height / 2);
        gsap.to(card, {
          rotationY: gsap.utils.clamp(-6, 6, ( cx / r.width)  * 12),
          rotationX: gsap.utils.clamp(-6, 6, (-cy / r.height) * 12),
          y: -8,
          scale: 1.02,
          boxShadow: '0 24px 48px -22px rgba(9,132,227,0.22), 0 12px 24px -16px rgba(168,0,255,0.12)',
          duration: 0.7, ease: 'power3.out', overwrite: 'auto',
        });
      };
      const onLeave = () => {
        gsap.to(card, {
          rotationX: 0, rotationY: 0, y: 0, scale: 1,
          boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
          duration: 1.1, ease: 'power3.out', overwrite: 'auto',
        });
      };
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
      return { card, onMove, onLeave };
    });

    return () => {
      entranceST.kill();
      gsap.killTweensOf(cards);
      handlers.forEach(({ card, onMove, onLeave }) => {
        card.removeEventListener('mousemove', onMove);
        card.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  /* ── Process: domino flip-in + number scramble + connecting line draw + magnetic hover ── */
  useEffect(() => {
    const wrap  = processWrapRef.current;
    const cards = processCardRefs.current.filter(Boolean);
    const nums  = processNumberRefs.current.filter(Boolean);
    if (!wrap || !cards.length) return;

    gsap.set(wrap, { perspective: 1400 });
    gsap.set(cards, {
      opacity: 0,
      rotationY: -95,
      transformOrigin: 'left center',
      y: 30,
      willChange: 'transform',
    });

    // Inject animated connecting line track (a thin gradient bar that "draws" beneath the cards)
    let line = wrap.querySelector('.process-line');
    if (!line) {
      line = document.createElement('div');
      line.className = 'process-line';
      line.style.cssText = [
        'position:absolute;left:0;right:0;top:50%;height:2px;',
        'background:linear-gradient(90deg, rgba(9,132,227,0.6), rgba(0,176,255,0.6), rgba(168,0,255,0.6));',
        'transform-origin:left center;transform:scaleX(0);',
        'pointer-events:none;border-radius:2px;z-index:0;opacity:0.55;',
        'display:none;',
      ].join('');
      wrap.appendChild(line);
      // Only show line on lg screens where cards are in a single row
      const mq = window.matchMedia('(min-width: 1024px)');
      const sync = () => { line.style.display = mq.matches ? 'block' : 'none'; };
      sync();
      mq.addEventListener?.('change', sync);
    }

    const dealTl = gsap.timeline({
      scrollTrigger: { trigger: wrap, start: 'top 80%', once: true },
    });

    dealTl
      .to(cards, {
        opacity: 1, rotationY: 0, y: 0,
        duration: 1.0, ease: 'back.out(1.4)',
        stagger: 0.15,
      })
      .to(line, { scaleX: 1, duration: 1.2, ease: 'power3.inOut' }, '-=0.9');

    // Number scramble: each "01"/"02" cycles random digits then snaps to its real value
    nums.forEach((el, i) => {
      const finalText = el.dataset.target || el.textContent;
      const obj = { v: 0 };
      gsap.to(obj, {
        v: 60,
        duration: 1.0,
        delay: 0.4 + i * 0.15,
        ease: 'power2.out',
        scrollTrigger: { trigger: wrap, start: 'top 80%', once: true },
        onUpdate: () => {
          if (obj.v < 55) {
            const n = Math.floor(Math.random() * 99);
            el.textContent = n.toString().padStart(2, '0');
          } else {
            el.textContent = finalText;
          }
        },
        onComplete: () => { el.textContent = finalText; },
      });
    });

    // Hover: card lifts in 3D, number pulses with glow
    const handlers = cards.map((card, i) => {
      const num = nums[i];
      const onMove = (e) => {
        const r = card.getBoundingClientRect();
        const cx = e.clientX - (r.left + r.width / 2);
        const cy = e.clientY - (r.top  + r.height / 2);
        gsap.to(card, {
          rotationY: gsap.utils.clamp(-10, 10, ( cx / r.width)  * 18),
          rotationX: gsap.utils.clamp(-10, 10, (-cy / r.height) * 18),
          y: -8, scale: 1.025,
          boxShadow: '0 26px 50px -22px rgba(9,132,227,0.30), 0 14px 28px -18px rgba(168,0,255,0.18)',
          duration: 0.5, ease: 'power3.out', overwrite: 'auto',
        });
        if (num) gsap.to(num, { scale: 1.08, duration: 0.45, ease: 'power3.out', overwrite: 'auto' });
      };
      const onLeave = () => {
        gsap.to(card, {
          rotationX: 0, rotationY: 0, y: 0, scale: 1,
          boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
          duration: 0.85, ease: 'elastic.out(1,0.55)', overwrite: 'auto',
        });
        if (num) gsap.to(num, { scale: 1, duration: 0.6, ease: 'elastic.out(1,0.5)', overwrite: 'auto' });
      };
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
      return { card, onMove, onLeave };
    });

    return () => {
      dealTl.kill();
      ScrollTrigger.getAll().forEach((t) => {
        if (t.trigger === wrap) t.kill();
      });
      handlers.forEach(({ card, onMove, onLeave }) => {
        card.removeEventListener('mousemove', onMove);
        card.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  return (
    <>
      {/* ─────────── HERO ─────────── */}
      <section ref={heroSectionRef} className="relative min-h-screen flex items-center bg-paper overflow-hidden pt-28 pb-20">
        {/* Cursor magnetic glow layer */}
        <div ref={heroGlowRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} />
        {/* Background mesh + dots */}
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />

        {/* Ambient blobs */}
        <motion.div
          className="absolute top-20 -left-32 w-[520px] h-[520px] rounded-full bg-nexcess-cyan/10 blur-3xl pointer-events-none"
          animate={{ scale: [1, 1.08, 1], x: [0, 20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 -right-40 w-[600px] h-[600px] rounded-full bg-nexcess-purple/10 blur-3xl pointer-events-none"
          animate={{ scale: [1, 1.06, 1], y: [0, -20, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />

        <div className="container-custom relative z-10 grid md:grid-cols-2 gap-16 items-center">

          {/* ── LEFT content — GSAP drives entrance, Framer Motion removed ── */}
          <div>
            {/* Status pill */}
            <div ref={heroBadgeRef}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-hairline shadow-soft mb-6"
            >
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-nexcess-blue opacity-60 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-nexcess-blue" />
              </span>
              <span className="text-[12px] font-medium text-ink-soft">Leading Tech Solutions Provider</span>
            </div>

            {/* Headline */}
            <h1 ref={heroH1Ref} className="display-hero">
              Building <span className="gradient-text">Scalable</span><br />
              Digital Solutions <br />
              for <span className="gradient-text">Modern Businesses</span>
            </h1>

            {/* Sub-copy */}
            <p ref={heroParaRef} className="narrative-lg mt-6 max-w-xl">
              We design and build scalable digital products that help brands grow,
              innovate, and stand out in a competitive world.
            </p>

            {/* CTAs */}
            <div ref={heroCtaRef} className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => navigate('/contact')} className="btn btn-primary">
                Get Started <HiArrowRight />
              </button>
              <button
                onClick={() => servicesRef.current?.scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-outline"
              >
                View Services
              </button>
            </div>

            {/* Trust row */}
            {/* <motion.div variants={fadeUp} custom={4} className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className={`w-9 h-9 rounded-full border-2 border-white bg-gradient-to-br ${i%2 ? 'from-nexcess-blue to-nexcess-cyan' : 'from-nexcess-purple to-nexcess-blue'}`} />
                ))}
              </div>
              <div>
                <div className="flex gap-0.5 text-nexcess-blue text-sm">★★★★★</div>
                <div className="text-xs text-ink-soft mt-0.5">Trusted by 200+ clients</div>
              </div>
            </motion.div> */}
          </div>

          {/* ── RIGHT — Hero image with floating cards ── */}
          <motion.div
            variants={scaleIn}
            initial="hidden"
            animate="show"
            className="relative"
          >
            {/* Outer glow ring */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-nexcess-blue/20 via-nexcess-cyan/10 to-nexcess-purple/20 blur-2xl scale-105 pointer-events-none" />

            {/* Image container — GSAP owns entrance & parallax */}
            <div
              ref={heroImgRef}
              className="relative rounded-3xl overflow-hidden shadow-soft-lg border border-white/60"
              style={{ aspectRatio: '4/5' }}
            >
              <img
                src={heroImage}
                alt="Nexcess Infotech"
                className="w-full h-full object-cover"
              />
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Floating card — top left */}
            <motion.div
              className="hero-float absolute -top-5 -left-6 glass rounded-2xl px-4 py-3.5 shadow-soft-lg hidden sm:flex items-center gap-3"
              initial={{ opacity: 0, x: -20, y: -10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
              style={{ willChange: 'transform' }}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-nexcess-purple to-nexcess-blue flex items-center justify-center text-white shrink-0">
                <HiSparkles size={18} />
              </div>
              <div>
                <div className="text-xs font-bold text-ink">AI Engine</div>
                <div className="text-[10px] text-ink-soft">Active · 99.9% uptime</div>
              </div>
            </motion.div>

            {/* Floating card — bottom right */}
            <motion.div
              className="hero-float absolute -bottom-5 -right-6 glass rounded-2xl px-4 py-3.5 shadow-soft-lg hidden sm:flex items-center gap-3"
              initial={{ opacity: 0, x: 20, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 1.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
              style={{ willChange: 'transform' }}
            >
              <div className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center text-green-500 shrink-0">
                <HiCheckCircle size={22} />
              </div>
              <div>
                <div className="text-xs font-bold text-ink">Project Delivered</div>
                <div className="text-[10px] text-ink-soft">2 min ago · on time</div>
              </div>
            </motion.div>

            {/* Floating stat pill — top right */}
            <motion.div
              className="hero-float absolute top-6 -right-5 glass rounded-2xl px-4 py-3 shadow-soft-lg hidden sm:block"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
            >
              <div className="text-2xl font-extrabold gradient-text leading-none">204+</div>
              <div className="text-[10px] text-ink-soft font-semibold mt-0.5">Projects Done</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─────────── STATS ─────────── */}
      <section ref={statsRef} className="bg-paper-2 border-y border-hairline">
        <div className="container-custom grid grid-cols-2 md:grid-cols-4 gap-8 py-14">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="stat-item text-center md:text-left opacity-0 transform translate-y-6"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="text-4xl md:text-5xl font-extrabold gradient-text tracking-tight">
                {stat.value}
              </div>
              <div className="text-sm text-ink-soft mt-2 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────── SERVICES ─────────── */}
      <section ref={servicesRef} className="section-padding bg-paper">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Our Services</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              Comprehensive <span className="gradient-text">Digital Services</span>
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">
              We offer a full spectrum of digital solutions tailored to help your business
              thrive in today's competitive landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: index * 0.07, duration: 0.5 }}
                className="holo-card-wrap"
                style={{ perspective: '900px' }}
                data-accent={service.accent}
              >
                {/* GSAP owns this inner div — Framer Motion only owns the outer */}
                <div
                  className="holo-card-inner group relative bg-white rounded-2xl p-7 border border-hairline shadow-soft flex flex-col h-full overflow-hidden"
                  style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
                >
                  {/* Colour tint — very light wash of card's own accent, no blend mode */}
                  <div
                    className={`holo-tint absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl pointer-events-none`}
                    style={{ opacity: 0, zIndex: 1 }}
                  />
                  {/* Glare — accent-coloured ellipse that tracks cursor */}
                  <div
                    className="holo-glare absolute inset-0 rounded-2xl pointer-events-none"
                    style={{ opacity: 0, zIndex: 2 }}
                  />

                  {/* Card content */}
                  <div className={`relative z-10 w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white text-2xl mb-5 shadow-soft group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                  <h3 className="heading-sm mb-3 relative z-10">{service.title}</h3>
                  <p className="narrative-body mb-5 relative z-10">{service.description}</p>
                  <Link
                    to="/services"
                    className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-nexcess-blue hover:gap-2 transition-all relative z-10"
                  >
                    Learn more <HiArrowRight />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── WHY CHOOSE US ─────────── */}
      <section ref={whyChooseRef} className="section-padding bg-paper-2 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-soft opacity-60 pointer-events-none" />
        <div className="container-custom relative z-10">
          <div className="grid md:grid-cols-2 gap-14 items-center">
            <div>
              <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Why Choose Us</div>
              <h2 data-reveal="wipe" className="heading-xl mb-5">
                Empowering Businesses with <span className="gradient-text">Innovative Technology</span>
              </h2>
              <p className="narrative-body mb-5">
                Nexcess Infotech is a leading digital agency with a passion for creating
                innovative solutions that help businesses thrive in the digital world. With
                over 5 years of experience, we have worked with clients across various
                industries to deliver exceptional results.
              </p>
              <p className="narrative-body mb-8">
                Our team of experts combines technical expertise with creative thinking to
                develop customized solutions that meet your unique business needs.
              </p>
              <button onClick={() => navigate('/about')} className="btn btn-primary">
                Learn More About Us <HiArrowRight />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {whyChoose.map((item) => (
                <div
                  key={item.title}
                  className="why-card bg-white rounded-2xl p-6 border border-hairline shadow-soft cursor-pointer"
                  style={{ willChange: 'transform' }}
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-nexcess-blue/10 to-nexcess-purple/10 flex items-center justify-center text-nexcess-blue text-lg mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-ink mb-1.5">{item.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── TESTIMONIALS ─────────── */}
      <section className="section-padding bg-paper-2">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Testimonials</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              What Our <span className="gradient-text">Clients Say</span>
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">
              Don't just take our word for it. Here's what our clients have to say about
              working with Nexcess Infotech.
            </p>
          </div>

          <div ref={testimonialsWrapRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                ref={(el) => { testimonialRefs.current[i] = el; }}
                className="bg-white rounded-2xl p-7 border border-hairline shadow-soft cursor-pointer"
                style={{ transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' }}
              >
                <div className="t-stars flex gap-1 text-nexcess-blue mb-4 inline-block origin-left">
                  {'★★★★★'}
                </div>
                <p className="text-ink leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-5 border-t border-hairline">
                  <img
                    src={t.image}
                    alt={t.author}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold text-ink">{t.author}</div>
                    <div className="text-xs text-ink-soft">{t.position}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── PROCESS ─────────── */}
      <section className="section-padding bg-paper">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Our Process</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              How We <span className="gradient-text">Bring Ideas</span> to Life
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">
              A clear, collaborative path from your first idea to a polished product.
            </p>
          </div>

          <div ref={processWrapRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {process.map((p, i) => (
              <div
                key={p.step}
                ref={(el) => { processCardRefs.current[i] = el; }}
                className="relative bg-white rounded-2xl p-7 border border-hairline shadow-soft cursor-pointer z-10"
                style={{ transformStyle: 'preserve-3d', backfaceVisibility: 'hidden', willChange: 'transform' }}
              >
                <div
                  ref={(el) => { processNumberRefs.current[i] = el; }}
                  data-target={p.step}
                  className="text-5xl font-extrabold gradient-text mb-4 leading-none inline-block origin-left"
                >
                  {p.step}
                </div>
                <h3 className="heading-sm mb-2">{p.title}</h3>
                <p className="text-sm text-ink-soft leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FINAL CTA ─────────── */}
      <section className="py-20 md:py-28 bg-paper">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center text-white shadow-soft-lg" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 50%, #d500f9 100%)' }}>
            {/* Decorative blobs */}
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />

            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs uppercase tracking-wider font-semibold mb-6">
                <HiSparkles /> Ready When You Are
              </div>
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-5 text-white">
                Transform Your Digital Presence.
              </h2>
              <p className="text-white/90 text-lg mb-9 max-w-xl mx-auto">
                Let's collaborate to bring your vision to life with our expertise in technology and design.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-nexcess-blue font-semibold shadow-soft hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300"
                >
                  Start Your Project <HiArrowRight />
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 backdrop-blur-sm text-white border border-white/30 font-semibold hover:bg-white/20 hover:-translate-y-0.5 transition-all duration-300"
                >
                  Schedule a Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
